class UITabs extends HTMLElement {
    constructor() {
        super()
        this.onClick = this.onClick.bind(this)
        this.onKeyDown = this.onKeyDown.bind(this)
    }

    connectedCallback() {
        if (this._initialized) return
        this._initialized = true

        this.isLightMode = this.getAttribute('data-light-mode') === 'true'
        this.indicator = this.querySelector('[data-indicator]')
        this.tabs = [...this.querySelectorAll('[role="tab"]')]

        this.activeTab =
            this.tabs.find(t => t.getAttribute('aria-selected') === 'true') ||
            this.tabs[0]

        this.tabs.forEach(tab => {
            tab.addEventListener('click', this.onClick)
            tab.addEventListener('keydown', this.onKeyDown)
        })

        this.applyStyles(this.activeTab)

        this.resizeObserver = new ResizeObserver(() => {
            this.updateIndicator()
        })

        this.resizeObserver.observe(this)
        this.tabs.forEach(tab => this.resizeObserver.observe(tab))

        if (document.fonts) {
            document.fonts.ready.then(() => this.updateIndicator())
        }

        this.updateIndicator()
    }

    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    }

    onClick(e) {
        this.activateTab(e.currentTarget)
    }

    onKeyDown(e) {
        const index = this.tabs.indexOf(document.activeElement)
        let newIndex = null

        if (e.key === 'ArrowRight') newIndex = (index + 1) % this.tabs.length
        if (e.key === 'ArrowLeft') newIndex = (index - 1 + this.tabs.length) % this.tabs.length
        if (e.key === 'Home') newIndex = 0
        if (e.key === 'End') newIndex = this.tabs.length - 1

        if (newIndex !== null) {
            e.preventDefault()
            this.tabs[newIndex].focus()
            this.activateTab(this.tabs[newIndex])
        }
    }

    activateTab(tab) {
        this.tabs.forEach(t => {
            const isActive = t === tab
            t.setAttribute('aria-selected', isActive)
            t.setAttribute('tabindex', isActive ? '0' : '-1')
        })

        this.activeTab = tab

        this.applyStyles(tab)
        this.animateIndicator(tab)

        this.dispatchEvent(
            new CustomEvent('ui:tabchange', {
                detail: { value: tab.dataset.value, tab },
                bubbles: true,
            })
        )
    }

    applyStyles(activeTab) {
        this.tabs.forEach(t => {
            const isActive = t === activeTab

            if (this.isLightMode) {
                t.classList.toggle('text-gray-600', isActive)
                t.classList.toggle('text-gray-500', !isActive)
                t.classList.remove('text-gray-200')
            } else {
                t.classList.toggle('text-gray-200', isActive)
                t.classList.toggle('text-gray-500', !isActive)
                t.classList.remove('text-gray-600')
            }
        })

        if (this.indicator) {
            this.indicator.classList.toggle('bg-gray-100', this.isLightMode)
            this.indicator.classList.toggle('bg-gray-600', !this.isLightMode)
        }
    }

    updateIndicator() {
        if (!this.indicator || !this.activeTab) return

        const { offsetLeft, offsetWidth } = this.activeTab

        if (!offsetWidth) return

        gsap.set(this.indicator, {
            left: offsetLeft,
            width: offsetWidth,
        })
    }

    animateIndicator(tab) {
        if (!this.indicator) return

        const { offsetLeft, offsetWidth } = tab

        if (!offsetWidth) return

        gsap.to(this.indicator, {
            left: offsetLeft,
            width: offsetWidth,
            duration: 0.3,
            ease: 'smooth',
        })
    }
}

customElements.define('ui-tabs', UITabs)